import { Badge, Icon, SlideOver } from '@uag/react-core';
import { clsx } from 'clsx';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { isSafeExternalUrl } from './isSafeExternalUrl';
import { KnownRiskLevel, riskLevelConfig, toKnownRiskLevel } from './riskLevel';
import { TrustScoreGauge } from './TrustScoreGauge';

import type {
    AiPlausibilityRiskLevel,
    AiPlausibilityResultModel,
    AiPlausibilityRiskSignalModel,
    AiPlausibilitySourceModel,
} from '@uag/welcome-shared/src/api/models';

const outlinedClassName = 'border-border-ultra-light rounded-md border border-solid';

const informationalSeverityIcon = { icon: 'info', className: 'text-icon-information' } as const;
type FindingIcon = typeof informationalSeverityIcon | (typeof riskLevelConfig)[KnownRiskLevel]['findingIcon'];

const findingIconBySeverity = {
    High: riskLevelConfig.high.findingIcon,
    Medium: riskLevelConfig.medium.findingIcon,
    Low: riskLevelConfig.low.findingIcon,
    Info: informationalSeverityIcon,
    Unknown: informationalSeverityIcon,
} as const satisfies Record<AiPlausibilityRiskLevel, FindingIcon>;

const toNumericScore = (score?: number | string | null) => {
    if (score === null || score === undefined || (typeof score === 'string' && score.trim() === '')) {
        return undefined;
    }

    const numericScore = typeof score === 'number' ? score : Number(score);
    return Number.isFinite(numericScore) ? numericScore : undefined;
};

const Section = ({ title, children }: PropsWithChildren<{ title: string }>) => (
    <section className="flex flex-col">
        <h3 className="text-text-light text-sm">{title}</h3>
        {children}
    </section>
);

const OutlinedList = ({ children }: PropsWithChildren) => (
    <ul className={clsx(outlinedClassName, 'divide-border-ultra-light divide-y overflow-hidden')}>{children}</ul>
);

const RiskBadge = ({ riskLevel }: { riskLevel: KnownRiskLevel }) => {
    const { t } = useTranslation();
    const { badge, labelKey } = riskLevelConfig[riskLevel];

    return <Badge {...badge}>{t(labelKey)}</Badge>;
};

const FindingItem = ({ finding }: { finding: AiPlausibilityRiskSignalModel }) => {
    const findingIcon = findingIconBySeverity[finding.severity];

    return (
        <li className="p-lg gap-sm flex items-start">
            <div className="flex h-6 items-center">
                <Icon {...findingIcon} fill />
            </div>
            <div className="flex flex-col text-base">
                {finding.signal.trim() && <span className="font-semibold">{finding.signal}</span>}
                {finding.annotation.trim() && <span className="text-text-light">{finding.annotation}</span>}
            </div>
        </li>
    );
};

const SourceItem = ({ source: { title, url } }: { source: AiPlausibilitySourceModel }) => {
    const label = title.trim() || url.trim();

    return (
        <li>
            {isSafeExternalUrl(url) ? (
                // eslint-disable-next-line react/forbid-elements -- external URL, not a router route
                <a
                    className="p-lg gap-md bg-background-white hover:bg-background-action-hover flex items-center text-base"
                    href={url}
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    <span className="grow">{label}</span>
                    <Icon aria-hidden="true" className="text-icon-default" icon="open_in_new" />
                </a>
            ) : (
                <div className="p-lg text-base">{label}</div>
            )}
        </li>
    );
};

type Props = {
    result: AiPlausibilityResultModel;
    opened: boolean;
    onClose: () => void;
};

export const AiPlausibilityDrawer = ({ result, opened, onClose }: Props) => {
    const { t } = useTranslation();
    const { overall, summary, riskSignals: findings, sources } = result;
    const riskLevel = toKnownRiskLevel(overall.riskLevel);
    const overallScore = toNumericScore(overall.overallScore);
    const trustScorePercent = overallScore === undefined ? undefined : overallScore * 100;
    const description = summary || overall.annotation;
    const visibleFindings = findings.filter(({ signal, annotation }) => signal.trim() || annotation.trim());
    const visibleSources = sources.filter(({ title, url }) => title.trim() || url.trim());

    return (
        <SlideOver opened={opened} size={400} title={t('aiPlausibilityCheck')} onClose={onClose}>
            <div className="gap-2xl flex flex-col">
                <div className="gap-lg flex flex-col">
                    <div className="flex flex-col items-start">
                        <h2 className="text-xl font-semibold">{t('aiPlausibilityValidationResult')}</h2>
                        {riskLevel && <RiskBadge riskLevel={riskLevel} />}
                    </div>
                    <Section title={t('aiPlausibilityTrustScore')}>
                        <div className={clsx(outlinedClassName, 'p-lg gap-lg flex flex-col items-center')}>
                            {trustScorePercent !== undefined && <TrustScoreGauge score={trustScorePercent} />}
                            {description && <p className="w-full text-base">{description}</p>}
                        </div>
                    </Section>
                </div>

                {visibleFindings.length > 0 && (
                    <Section title={t('aiPlausibilityKeyFindings')}>
                        <OutlinedList>
                            {visibleFindings.map((finding, index) => (
                                <FindingItem key={`${finding.category}-${index}`} finding={finding} />
                            ))}
                        </OutlinedList>
                    </Section>
                )}

                {visibleSources.length > 0 && (
                    <Section title={t('aiPlausibilitySources')}>
                        <OutlinedList>
                            {visibleSources.map((source, index) => (
                                <SourceItem key={`${source.url}-${index}`} source={source} />
                            ))}
                        </OutlinedList>
                    </Section>
                )}
            </div>
        </SlideOver>
    );
};
